<template>
  <div class="latex-demo">
    <div class="demo-container">
      <div class="output-section">
        <div id="customOutput" v-html="parsedLatex"></div>
      </div>
    </div>
  </div>
</template>

<script>
// 1. 本地导入 KaTeX 核心样式（必须导入，否则公式样式错乱）
import 'katex/dist/katex.min.css'
// 2. 本地导入 KaTeX 自动渲染方法（替代 CDN 的 auto-render.min.js）
import renderMathInElement from 'katex/dist/contrib/auto-render.min.js'

export default {
  name: 'LatexRenderer',
  props: {
    // 接收外部传入的 LaTeX 内容（对应 rulesForm.explanation）
    latexContent: {
      type: String, // 数据类型为字符串
      default: '',  // 默认值为空字符串，避免报错
      required: false // 非必填，兼容无传值场景
    }
  },
  data() {
    return {
      // 解析后的内容
      parsedLatex: ''
    }
  },
  mounted() {
    // 初始化 KaTeX 自动渲染配置
    this.initKaTeXConfig()
    this.renderLatex()
  },
  methods: {
    // 渲染用户输入的 LaTeX
    renderLatex() {
      // 第一步：先处理原生 Markdown 格式（\n\n/ \n → <br>、**、###）
      const markdownHandled = this.handleMarkdownNative(this.latexContent)
      // 第二步：再处理 LaTeX 转义
      this.parsedLatex = this.parseLaTeX(markdownHandled)

      this.$nextTick(() => {
        // 调用本地导入的 renderMathInElement 方法，渲染公式
        renderMathInElement(document.getElementById('customOutput'), this.getKaTeXOptions())
      })
    },

    // 原生处理 Markdown 格式（核心：\n\n 统一替换为 <br>）
    handleMarkdownNative(text) {
      if (!text) return ''
      let result = text

      // 1. 处理标题：### 标题 → <h3>标题</h3>（支持 #、##、###）
      result = result.replace(/^###\s+(.*?)$/gm, '<h3>$1</h3>')
      result = result.replace(/^##\s+(.*?)$/gm, '<h2>$1</h2>')
      result = result.replace(/^#\s+(.*?)$/gm, '<h1>$1</h1>')

      // 2. 处理加粗：**内容** → <strong>内容</strong>
      result = result.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')

      // 3. 核心修改：所有换行（\n\n、\n）统一替换为单个 <br>
      // 先匹配所有连续换行（包括 \n\n、\n\n\n 等），替换为单个 <br>
      result = result.replace(/\n\n+/g, '<br>')

      return result
    },

    // 解析 LaTeX 内容（无修改，兼容换行处理结果）
    parseLaTeX(text) {
      if (!text) return ''
      return text
          .replace(/\\\[/g, '\\[')
          .replace(/\\\]/g, '\\]')
          .replace(/\\\(/g, '\\(')
          .replace(/\\\)/g, '\\)')
          // 专门保留 \boxed 命令（避免反斜杠丢失）
          .replace(/\\\\boxed/g, '\\boxed');
    },

    // 初始化 KaTeX 配置（无修改）
    initKaTeXConfig() {
      this.$nextTick(() => {
        renderMathInElement(this.$el, this.getKaTeXOptions())
      })
    },

    // 提取 KaTeX 公共配置（无修改）
    getKaTeXOptions() {
      return {
        delimiters: [
          { left: "\\[", right: "\\]", display: true }, // 块级公式
          { left: "\\(", right: "\\)", display: false } // 行内公式
        ],
        throwOnError: false, // 忽略语法错误，避免页面崩溃
        strict: 'ignore' // 忽略不规范语法，提升兼容性
      }
    }
  }
}
</script>

<style scoped>
/* 组件样式隔离，保留原有功能 */
.latex-demo {
  font-family: 'Segoe UI', Arial, sans-serif;
  max-width: 100%;
  padding: 20px;
  line-height: 1.6;
  background-color: #fff6ec;
}
.demo-container {
  border-radius: 8px;
  padding: 0px;
  margin-top: 0px;
}
.output-section {
  padding: 15px;
  border-radius: 4px;
  height: auto;
  background-color: #fff6ec;
}
/* 穿透 scoped 样式，作用于 KaTeX 动态生成的元素 */
::v-deep .katex-display {
  margin: 1em 0;
  overflow-x: auto;
  overflow-y: hidden;
}
/* Markdown 生成元素样式美化 */
::v-deep h1,
::v-deep h2,
::v-deep h3 {
  margin: 1rem 0 0.5rem;
  font-weight: 600;
  color: #333;
}
::v-deep strong {
  font-weight: 700;
  color: #222;
}
/* 可选：给 <br> 添加少量间距，让换行更易阅读（不修改也可实现核心需求） */
::v-deep br {
  display: block;
  margin: 0.2rem 0;
  line-height: 1.2;
}
button {
  padding: 8px 16px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
  transition: background-color 0.2s;
}
button:hover {
  background-color: #2980b9;
}
</style>